import React from "react";
import {
  BarChart,
  Bar,
  Brush,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  Tooltip,
  LabelList,
  ResponsiveContainer,
} from "recharts";
import { useMediaQuery } from "react-responsive";
import { ChartWrapper } from "../components/ChartWrapper";
import { formatNumber } from "../../utils/number";
import { formatLabel } from "../../utils/string";

// 🔥 Smart label
const renderSmartLabel = (props) => {
  const { x, y, width, height, value } = props;

  if (!value || value === 0) return null;

  if (height < 18) {
    return (
      <text
        x={x + width / 2}
        y={y - 4}
        fill="#111"
        textAnchor="middle"
        fontSize={12}
        fontWeight="600"
      >
        {formatNumber(value)}
      </text>
    );
  }

  return (
    <text
      x={x + width / 2}
      y={y + height / 2}
      fill="#fff"
      textAnchor="middle"
      dominantBaseline="middle"
      fontSize={12}
      fontWeight="700"
    >
      {formatNumber(value)}
    </text>
  );
};

export default function CognitiveReport({ data = [], loading }) {
  const isSmall = useMediaQuery({ query: "(max-width: 600px)" });

  return (
    <ChartWrapper
      title="Cognitive Skill Distribution"
      subtitle="Breakdown of correctly attempted questions by cognitive levels."
      loading={loading}
      isEmpty={!Boolean(data?.length)}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          barCategoryGap={isSmall ? "10%" : "15%"}
          margin={{
            top: 10,
            right: 20,
            left: 20,
            bottom: 85,
          }}
        >
          {/* Grid */}
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />

          {/* X Axis */}
          <XAxis
            tickFormatter={value => formatLabel(value)}
            dataKey="Subject"
            interval={0}
            tick={{
              fontSize: isSmall ? 11 : 13,
              fill: "#222",
              fontWeight: 600,
            }}
            axisLine={{ stroke: "#9ca3af", strokeWidth: 1.2 }}
            tickLine={false}
            angle={isSmall ? -25 : 0}
            textAnchor={isSmall ? "end" : "middle"}
          />

          {/* Y Axis */}
          <YAxis
            tick={{
              fontSize: 12,
              fill: "#222",
              fontWeight: 600,
            }}
            tickFormatter={(value) => formatNumber(value)}
            axisLine={{ stroke: "#9ca3af", strokeWidth: 1.2 }}
            tickLine={false}
            width={60}
            label={{
              value: "Questions",
              angle: -90,
              position: "outsideLeft",
              dx: -30,
              style: {
                fontSize: 13,
                fontWeight: 700,
                fill: "#111",
              },
            }}
          />

          {/* Tooltip */}
          <Tooltip
            formatter={(value) => formatNumber(value)}
            contentStyle={{
              borderRadius: "10px",
              border: "none",
              boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
              fontSize: "13px",
            }}
          />

          {/* Brush */}
          <Brush
            dataKey="Subject"
            height={10}
            endIndex={Math.min(data.length - 1, 2)}
            stroke="#6b7280"
          />

          {/* Bars */}
          <Bar dataKey="Knowledge" fill="#06b6d4" maxBarSize={50}>
            <LabelList content={renderSmartLabel} />
          </Bar>

          <Bar dataKey="Understanding" fill="#8b5cf6" maxBarSize={50}>
            <LabelList content={renderSmartLabel} />
          </Bar>

          <Bar dataKey="Analysis" fill="#f59e0b" maxBarSize={50}>
            <LabelList content={renderSmartLabel} />
          </Bar>

          <Bar dataKey="Application" fill="#ef4444" maxBarSize={50}>
            <LabelList content={renderSmartLabel} />
          </Bar>

          {/* Legend */}
          <Legend
            wrapperStyle={{
              fontSize: "16px",
              fontWeight: 600,
              paddingTop: 10,
            }}
          />
        </BarChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
}